import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getPresignedUrl } from '../../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { EXT_MIME, PREVIEWABLE_MIME_TYPES, SPREADSHEET_TYPES } from '../../../DocumentViewer/constants';
import { classifyAddable, addFileToReports } from '../../../Reports/addFileToReports';
import { showAddedToReportsToast } from '../../../Reports/addedToReportsToast';
import { useAddToReportsVisible } from '../../../Reports/useAddToReportsVisible';
import { useAppSelector } from '../../../../app/hooks';
import { MagicUploadFileData } from '../../useFetchDataForMU';

export type MagicUploadOpenDrawer = 'docx' | 'sheet' | 'file' | null;

/**
 * Data-Room-parity file-open + Add-to-Reports logic for the Magic Upload
 * files table — mirrors useFetchDataRoomFiles's handleFileClick /
 * handleAddToReports. Routes by extension-first MIME resolution, same as Data
 * Room: docx/txt -> DocxDrawer, xlsx/xls/csv -> SheetEditorDrawer (view-only,
 * no save context), pdf/image -> FileViewerDrawer, anything else -> the
 * caller's existing download behaviour. `downloadTheFile` is MU's own
 * download helper (useFetchDataForMU) — reused here, not duplicated.
 */
const useMagicUploadFileOpen = (downloadTheFile: (docId: number | string, name: string) => Promise<void>) => {
    const [activeFile, setActiveFile] = useState<MagicUploadFileData | null>(null);
    const [openDrawer, setOpenDrawer] = useState<MagicUploadOpenDrawer>(null);
    const [fileUrl, setFileUrl] = useState('');
    const [fileMimeType, setFileMimeType] = useState('');
    const [addingToReports, setAddingToReports] = useState(false);
    const addingToReportsRef = useRef(false);
    const [addedToReports, setAddedToReports] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );

    const user = useAppSelector((state) => state.user.userData);
    const addToReportsVisible = useAddToReportsVisible();

    const closeFile = () => {
        setOpenDrawer(null);
        setActiveFile(null);
        setFileUrl('');
        setFileMimeType('');
    };

    const openFile = async (file: MagicUploadFileData) => {
        const res = await getPresignedUrl({ docId: Number(file.docId) }, false);
        if (!res) {
            toast.error('Error in fetching file');
            return;
        }
        const ext = file.fileName.split('.').pop()?.toLowerCase() ?? '';
        const apiMimeType = res.fileType?.split(';')[0]?.trim().toLowerCase() ?? '';
        // Extension wins for known types, same fallback order as Data Room's handleFileClick.
        const effectiveMimeType = EXT_MIME[ext] ?? apiMimeType;

        if (effectiveMimeType === EXT_MIME.docx || effectiveMimeType === EXT_MIME.txt) {
            setOpenDrawer('docx');
        } else if (SPREADSHEET_TYPES.includes(effectiveMimeType)) {
            setOpenDrawer('sheet');
        } else if (PREVIEWABLE_MIME_TYPES.includes(effectiveMimeType) || effectiveMimeType?.startsWith('image/')) {
            setOpenDrawer('file');
        } else {
            await downloadTheFile(file.docId, file.fileName);
            return;
        }
        setActiveFile(file);
        setFileUrl(res.uri);
        setFileMimeType(effectiveMimeType);
    };

    /** Same gate for the row three-dots "Add to Reports" item and the open
     *  drawers' primary header button — untagged files (no orgId) never qualify. */
    const isAddable = (file: MagicUploadFileData): boolean =>
        addToReportsVisible && !!file.orgId && classifyAddable(file.fileName) !== null;

    const handleAddToReports = async (file: MagicUploadFileData) => {
        if (!isAddable(file)) return;
        if (addingToReportsRef.current) return;
        addingToReportsRef.current = true;
        setAddingToReports(true);
        try {
            const res = await getPresignedUrl({ docId: Number(file.docId) }, false);
            if (!res) {
                toast.error('Error in fetching file');
                return;
            }
            const fileRes = await fetch(res.uri);
            if (fileRes.status !== 200) {
                toast.error('Error in fetching file');
                return;
            }
            const blob = await fileRes.blob();
            await addFileToReports({
                file: blob,
                fileName: file.fileName,
                fileType: res.fileType,
                assesseeOrgId: file.orgId,
                investorOrgId: user?.investorOrgId,
                createdBy: user?.primaryEmail,
            });
            showAddedToReportsToast(
                () => window.open(`/investor/app/reports/${file.orgId}`, '_blank', 'noopener,noreferrer'),
                () => {
                    if (isMountedRef.current) setAddedToReports(false);
                },
            );
            setAddedToReports(true);
        } catch (err) {
            console.error('Failed to add Magic Upload file to Reports:', err instanceof Error ? err.message : 'unknown error');
            toast.error('Failed to add this file to Reports. Please try again.');
        } finally {
            addingToReportsRef.current = false;
            setAddingToReports(false);
        }
    };

    return {
        activeFile,
        openDrawer,
        fileUrl,
        fileMimeType,
        openFile,
        closeFile,
        isAddable,
        handleAddToReports,
        addingToReports,
        addedToReports,
    };
};

export default useMagicUploadFileOpen;
